import React, { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { Snackbar, styled } from "@mui/material";
import Header from "@shared/header";
import BasicDetails, {
  BasicDetailsFormDefaultValues,
  IBasicDetailsForm,
} from "./basicDetails";
import BankDetails, {
  BankDetailsFormDefaultValues,
  IBankDetailsForm,
} from "./bankDetails";
import ConfirmDetails from "./confirmDetails";
import Wizard from "./wizardSteps";
import { getUrl } from "utils/core";
import { APIResource, KYCConstants } from "constants/application";
import { useDispatch, useSelector } from "react-redux";
import {
  IGenericInformation,
  setGenericInformation,
  setKycDone,
} from "store/slices/genericInformation";
import { IBankBranchDetails, setBankDetails } from "store/slices/ifscSlice";
import { setPanResponse, setPanStatus } from "store/slices/validatePAN";
import { setBankResponse, setBankStatus } from "store/slices/validateBank";
import axiosInstance from "utils/axiosInstance";
import Loader from "@shared/loader";
import { PAN_STATUS_VARIANTS } from "constants/panStatusVariants";
import { BANK_STATUS_VARIANTS } from "constants/bankStatusVariants";
import { KYC_STATUS_VARIANTS } from "constants/kycStatusVariants";
import { ROUTES_VARIANT } from "constants/routesVariant";

const Container = styled("div")`
  display: flex;
  flex-direction: column;
  height: inherit;
`;

export interface IAllData extends IBasicDetailsForm, IBankDetailsForm {}

// Process COmponent which contains 3 step of KYC verification process.
function KYCsteps() {
  const [step, setStep] = useState(-1);
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>("");
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const info = useSelector((state: any) => state?.info?.allData);
  const [formData, setFormData] = useState<IAllData>({
    ...BasicDetailsFormDefaultValues,
    ...BankDetailsFormDefaultValues,
  });

  useEffect(() => {
    // To get the customer details from the API and navigate user to appropriate step based on the kycStatus key
    const getCustomerDetails = async () => {
      setError("");
      setLoading(true);
      try {
        const appName = localStorage.getItem('appname');
        const response = await axiosInstance.get(
          getUrl(APIResource.customerDetails, [
            {
              variableName: "customerId",
              variableValue: info?.customerId,
            },
          ],  [{paramName: "customerSource", paramValue:  appName?appName:'BRP'}])
        );
        if (response.status === 200 && response.data) {
          if (response.data?.error) {
            setError(response.data?.message);
          } else {
            const data = response.data as IGenericInformation;
            dispatch(setGenericInformation(data));
            switch (data.kycStatus) {
              case KYC_STATUS_VARIANTS.PENDING:
                setStep(1);
                setSearchParams(
                  {
                    step: "1",
                  },
                  { replace: true }
                );
                break;
              case KYC_STATUS_VARIANTS.PAN_VERIFIED:
                // User has verified the PAN update form and navigate user to bank verification step
                setStep(2);
                setSearchParams(
                  {
                    step: "2",
                  },
                  { replace: true }
                );
                // set default form values
                setFormData((prev) => {
                  const newFormData: IAllData = {
                    ...prev,
                    mobileNumber: data?.mobileNumber,
                    panNumber: data?.panDetails?.panNumber || "",
                    establishmentType: data.establishmentType,
                    tdsStatus: false,
                  };
                  return { ...newFormData };
                });
                panStatusSetting(data);
                break;
              case KYC_STATUS_VARIANTS.BANK_VERIFIED:
                // User has verified the Bank, update form and navigate user to confirm details step
                setStep(3);
                setSearchParams(
                  {
                    step: "3",
                  },
                  { replace: true }
                );
                // set form default values
                setFormData((prev) => {
                  const newFormData: IAllData = {
                    ...prev,
                    mobileNumber: data.mobileNumber,
                    panNumber: data?.panDetails?.panNumber || "",
                    establishmentType: data.establishmentType,
                    tdsStatus: data?.panDetails ? false : true,
                    bankAccountNumber: data?.bankDetails?.bankAccountNumber,
                    reEnterBankAccountNumber:
                      data?.bankDetails?.bankAccountNumber,
                    accType: data?.bankDetails?.accountType,
                    ifscCode: {
                      ifscCode: data?.bankDetails?.ifscCode,
                      bank: data?.bankDetails?.bankName,
                      address: data?.bankDetails?.bankAddress,
                    },
                    accountProofDocumentUrl: data?.bankDetails?.accountProofDocumentUrl,
                  };
                  return { ...newFormData };
                });
                // eslint-disable-next-line no-case-declarations
                const ifscDetails: IBankBranchDetails = {
                  ifscCode: data?.bankDetails?.ifscCode,
                  bank: data?.bankDetails?.bankName,
                  address: data?.bankDetails?.bankAddress,
                };
                if (data?.panDetails) {
                  panStatusSetting(data);
                } else {
                  dispatch(
                    setPanStatus({
                      value: PAN_STATUS_VARIANTS.PAN_UNAVAILABLE_CHECKED,
                    })
                  );
                  dispatch(setPanResponse({ value: null }));
                }
                dispatch(setBankDetails({ value: ifscDetails }));
                dispatch(setBankResponse({ value: data.bankDetails }));
                dispatch(
                  setBankStatus({ value: BANK_STATUS_VARIANTS.BANK_VERIFIED })
                );
                break;
              case KYC_STATUS_VARIANTS.PAN_NOT_SUBMITTED:
                setStep(2);
                setSearchParams(
                  {
                    step: "2",
                  },
                  { replace: true }
                );
                setFormData((prev) => {
                  const newFormData: IAllData = {
                    ...prev,
                    panNumber: "",
                    establishmentType: data.establishmentType,
                    tdsStatus: true,
                  };
                  return { ...newFormData };
                });
                dispatch(
                  setPanStatus({
                    value: PAN_STATUS_VARIANTS.PAN_UNAVAILABLE_CHECKED,
                  })
                );
                dispatch(setPanResponse({ value: null }));
                break;
              case KYC_STATUS_VARIANTS.VERIFIED:
              case KYC_STATUS_VARIANTS.VERIFIED_NOT_SUBMITTED:
                dispatch(setKycDone(true) as any);
                navigate(`/${ROUTES_VARIANT.FINISH}`, { replace: true });
                break;
              default:
                // KYC Status unrecognized
                console.log("KYC Status unrecognized");
            }
          }
        }
      } catch (err) {
        setError("Error Occurred while fetching customer details!");
        console.error("Error Occurred ", err);
      }
      setLoading(false);
    };

    // only if user comes through OTP login, allow KYC steps, else navigate user to home page
    if (info?.customerId) {
      getCustomerDetails();
    } else {
      navigate("/", { replace: true });
    }
  }, []);

  // on change of url search param step value, set the step value using useeffect
  useEffect(() => {
    // only if user comes through OTP login, KYC step is relevant
    if (info?.customerId) {
      const _step = Number(location.search.split("=")[1]);
      if (_step) {
        setStep(_step);
      }
    }
  }, [searchParams]);

  function panStatusSetting(responseData: IGenericInformation) {
    if (!responseData.panDetails.isCompliant) {
      dispatch(setPanStatus({ value: PAN_STATUS_VARIANTS.PAN_INACTIVE }));
    } else if (
      String(responseData?.panDetails?.panOperationalStatus).toUpperCase() ===
      "INOPERATIVE"
    ) {
      dispatch(setPanStatus({ value: PAN_STATUS_VARIANTS.PAN_INOPERATIVE }));
    } else {
      dispatch(setPanStatus({ value: PAN_STATUS_VARIANTS.PAN_VERIFIED }));
    }
    dispatch(setPanResponse({ value: responseData?.panDetails }));
  }
  // Step navigation
  const nextStep = useCallback(
    (partialData: IBankDetailsForm | IBasicDetailsForm) => {
      setFormData((prev) => ({ ...prev, ...partialData }));
      const _next = Math.min(step + 1, 3);
      setStep(_next);
      setSearchParams(
        {
          step: String(_next),
        },
        { replace: true }
      );
    },
    [step, setSearchParams]
  );

  return (
    <Container>
      <Header />
      <Wizard step={step} />
      {step === 1 && <BasicDetails formData={formData} nextStep={nextStep} />}
      {step === 2 && <BankDetails formData={formData} nextStep={nextStep} />}
      {step === 3 && <ConfirmDetails />}
      {loading && <Loader />}
      <Snackbar
        open={Boolean(error)}
        onClose={() => setError("")}
        autoHideDuration={KYCConstants.autoHideDuration}
        message={error}
        sx={{ bottom: { xs: 90, sm: 0 } }}
      />
    </Container>
  );
}

export default KYCsteps;
